// Signature framework diagrams as SVG strings. Each framework's `hero` selects
// a shape; `quad` / `nodes` provide the data. All output is plain SVG markup.
import type { Framework, Quad } from "../shared/content";

function esc(s: string): string {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Wrap a label into up to `maxLines` lines of roughly `max` chars.
function wrap(text: string, max: number, maxLines = 3): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if (cur && (cur + " " + w).length > max) {
      lines.push(cur);
      cur = w;
    } else cur = cur ? cur + " " + w : w;
  }
  if (cur) lines.push(cur);
  return lines.slice(0, maxLines);
}

function multilineText(lines: string[], cx: number, cy: number, fill: string, size: number, weight = "600"): string {
  const lh = size + 3;
  const startY = cy - ((lines.length - 1) * lh) / 2;
  return lines
    .map(
      (ln, i) =>
        `<text x="${cx}" y="${startY + i * lh}" text-anchor="middle" dominant-baseline="middle" font-family="'EB Garamond',serif" font-size="${size}" font-weight="${weight}" fill="${fill}">${esc(ln)}</text>`
    )
    .join("");
}

// ----------------------------------------------------------- Clark stairs
export function svgStairs(): string {
  const steps = [
    { n: "1", t: "Inclusion", s: "Accepted for who you are" },
    { n: "2", t: "Learner", s: "Safe to ask & to err" },
    { n: "3", t: "Contributor", s: "Trusted to add value" },
    { n: "4", t: "Challenger", s: "Safe to question" },
  ];
  const W = 660,
    baseY = 250,
    stepW = 150,
    gap = 8;
  let bars = "";
  steps.forEach((st, i) => {
    const h = 60 + i * 48;
    const x = 12 + i * (stepW + gap);
    const y = baseY - h;
    const fill = ["#cfd9ea", "#a9b9d6", "#7d93c0", "#002147"][i];
    const tc = i === 3 ? "#fff" : "#2A2520";
    bars += `<rect x="${x}" y="${y}" width="${stepW}" height="${h}" rx="6" fill="${fill}"/>`;
    bars += `<text x="${x + 14}" y="${y + 26}" font-family="'Cormorant Garamond',serif" font-size="22" font-weight="600" fill="${tc}">${st.n}</text>`;
    bars += `<text x="${x + 14}" y="${y + 48}" font-family="'EB Garamond',serif" font-size="15" font-weight="600" fill="${tc}">${st.t}</text>`;
    bars += `<text x="${x + 14}" y="${baseY + 22}" font-family="'EB Garamond',serif" font-size="11.5" fill="#6b6354">${st.s}</text>`;
  });
  return `<svg viewBox="0 0 ${W} 290" width="100%" xmlns="http://www.w3.org/2000/svg"><defs><marker id="ah" markerWidth="9" markerHeight="9" refX="6" refY="4.5" orient="auto"><path d="M1 1l6 3.5-6 3.5z" fill="#6D1D2C"/></marker></defs><line x1="14" y1="38" x2="640" y2="38" stroke="#6D1D2C" stroke-width="1.4" marker-end="url(#ah)" stroke-dasharray="3 4"/><text x="14" y="28" font-family="'EB Garamond',serif" font-style="italic" font-size="13" fill="#6D1D2C">deeper permission →</text>${bars}</svg>`;
}

// ----------------------------------------------------------- 2×2 quad
export function svgQuad(q: Quad): string {
  const cell = (x: number, y: number, label: string, fill: string) =>
    `<rect x="${x}" y="${y}" width="160" height="110" fill="${fill}" stroke="#C7B79B"/>` +
    multilineText(wrap(label, 18, 3), x + 80, y + 55, "#2A2520", 18, "600");
  return `<svg viewBox="0 0 620 300" width="100%" xmlns="http://www.w3.org/2000/svg">
    <text x="310" y="16" text-anchor="middle" font-family="'EB Garamond',serif" font-style="italic" font-size="13" fill="#6D1D2C">${esc(q.y || "")}</text>
    <text x="310" y="296" text-anchor="middle" font-family="'EB Garamond',serif" font-style="italic" font-size="13" fill="#6D1D2C">${esc(q.x || "")}</text>
    ${cell(150, 32, q.tl, "rgba(0,33,71,.05)")}
    ${cell(310, 32, q.tr, "rgba(109,29,44,.05)")}
    ${cell(150, 142, q.bl, "rgba(109,29,44,.04)")}
    ${cell(310, 142, q.br, "rgba(0,33,71,.04)")}
  </svg>`;
}

// ----------------------------------------------------------- change equation
export function svgChangeEquation(): string {
  const term = (x: number, label: string, sub: string, fill: string, lc: string) => `<g>
    <rect x="${x}" y="70" width="112" height="78" rx="8" fill="${fill}" stroke="#C7B79B"/>
    <text x="${x + 56}" y="104" text-anchor="middle" font-family="'Cormorant Garamond',serif" font-size="26" font-weight="600" fill="${lc}">${label}</text>
    <text x="${x + 56}" y="128" text-anchor="middle" font-family="'EB Garamond',serif" font-size="12" fill="#6b6354">${sub}</text></g>`;
  const op = (x: number, s: string) => `<text x="${x}" y="118" text-anchor="middle" font-family="'Cormorant Garamond',serif" font-size="30" font-weight="600" fill="#6D1D2C">${esc(s)}</text>`;
  return `<svg viewBox="0 0 620 200" width="100%" xmlns="http://www.w3.org/2000/svg">
    ${term(6, "D", "dissatisfaction", "rgba(0,33,71,.05)", "#002147")} ${op(132, "×")}
    ${term(150, "V", "vision", "rgba(0,33,71,.05)", "#002147")} ${op(276, "×")}
    ${term(294, "N", "next steps", "rgba(0,33,71,.05)", "#002147")} ${op(420, ">")}
    ${term(440, "R", "resistance", "rgba(109,29,44,.06)", "#6D1D2C")}
    <text x="310" y="184" text-anchor="middle" font-family="'EB Garamond',serif" font-style="italic" font-size="13" fill="#6D1D2C">multiplicative — any factor near zero stalls the change</text>
  </svg>`;
}

// ----------------------------------------------------------- trust equation
export function svgTrustEquation(): string {
  const box = (x: number, y: number, w: number, label: string, sub: string) =>
    `<rect x="${x}" y="${y}" width="${w}" height="58" rx="8" fill="rgba(0,33,71,.05)" stroke="#C7B79B"/>` +
    `<text x="${x + w / 2}" y="${y + 26}" text-anchor="middle" font-family="'Cormorant Garamond',serif" font-size="20" font-weight="600" fill="#002147">${esc(label)}</text>` +
    `<text x="${x + w / 2}" y="${y + 46}" text-anchor="middle" font-family="'EB Garamond',serif" font-size="11" fill="#6b6354">${esc(sub)}</text>`;
  return `<svg viewBox="0 0 620 230" width="100%" xmlns="http://www.w3.org/2000/svg">
    <text x="40" y="92" text-anchor="middle" font-family="'Cormorant Garamond',serif" font-size="22" font-weight="600" fill="#2A2520">T</text>
    <text x="40" y="112" text-anchor="middle" font-family="'EB Garamond',serif" font-size="11" fill="#6b6354">trust</text>
    <text x="78" y="98" text-anchor="middle" font-family="'Cormorant Garamond',serif" font-size="28" font-weight="600" fill="#6D1D2C">=</text>
    <line x1="110" y1="100" x2="560" y2="100" stroke="#6D1D2C" stroke-width="1.6"/>
    ${box(118, 36, 130, "Credibility", "what they say")}
    <text x="262" y="72" text-anchor="middle" font-family="'Cormorant Garamond',serif" font-size="24" fill="#6D1D2C">+</text>
    ${box(280, 36, 130, "Reliability", "what they do")}
    <text x="424" y="72" text-anchor="middle" font-family="'Cormorant Garamond',serif" font-size="24" fill="#6D1D2C">+</text>
    ${box(440, 36, 120, "Intimacy", "feels safe")}
    ${box(280, 130, 130, "Self-orientation", "focus on self")}
    <text x="310" y="206" text-anchor="middle" font-family="'EB Garamond',serif" font-style="italic" font-size="13" fill="#6D1D2C">the denominator divides — lower self-orientation first</text>
  </svg>`;
}

// ----------------------------------------------------------- layered bands
export function svgLayers(nodes: string[]): string {
  const n = Math.max(1, nodes.length);
  const H = 70 + n * 56;
  const fills = ["#cfd9ea", "#a9b9d6", "#7d93c0", "#5a73a8", "#002147"];
  let bands = "";
  nodes.forEach((label, i) => {
    const y = 24 + i * 56;
    const fill = fills[Math.min(i, fills.length - 1)];
    const tc = i >= 3 ? "#fff" : "#2A2520";
    bands += `<rect x="40" y="${y}" width="540" height="46" rx="6" fill="${fill}"/>`;
    bands += `<text x="310" y="${y + 28}" text-anchor="middle" font-family="'EB Garamond',serif" font-size="16" font-weight="600" fill="${tc}">${esc(label)}</text>`;
  });
  return `<svg viewBox="0 0 620 ${H}" width="100%" xmlns="http://www.w3.org/2000/svg">${bands}</svg>`;
}

// ----------------------------------------------------------- pyramid tiers
export function svgPyramid(nodes: string[]): string {
  const n = Math.max(1, nodes.length);
  const H = 40 + n * 52;
  const cx = 310;
  const topW = 150,
    botW = 540;
  let tiers = "";
  const fills = ["#002147", "#5a73a8", "#7d93c0", "#a9b9d6", "#cfd9ea"];
  nodes.forEach((label, i) => {
    const y = 24 + i * 52;
    const wTop = topW + ((botW - topW) * i) / n;
    const wBot = topW + ((botW - topW) * (i + 1)) / n;
    const fill = fills[Math.min(i, fills.length - 1)];
    const tc = i <= 1 ? "#fff" : "#2A2520";
    tiers += `<path d="M${cx - wTop / 2} ${y} L${cx + wTop / 2} ${y} L${cx + wBot / 2} ${y + 46} L${cx - wBot / 2} ${y + 46} Z" fill="${fill}" stroke="#fff" stroke-width="1.5"/>`;
    tiers += `<text x="${cx}" y="${y + 28}" text-anchor="middle" font-family="'EB Garamond',serif" font-size="14" font-weight="600" fill="${tc}">${esc(label)}</text>`;
  });
  return `<svg viewBox="0 0 620 ${H}" width="100%" xmlns="http://www.w3.org/2000/svg">${tiers}</svg>`;
}

// ----------------------------------------------------------- change curve
export function svgCurve(nodes: string[]): string {
  const n = Math.max(2, nodes.length);
  const W = 660,
    H = 240,
    left = 40,
    right = 620,
    top = 30,
    bottom = 170;
  // Morale/performance profile: start mid, drop into the dip (~35%), then
  // recover above the starting point — the classic transition curve.
  const profile = nodes.map((_, i) => {
    const t = i / (n - 1);
    if (t <= 0.2) return 0.55 - t * 0.5; // slight pre-dip
    if (t <= 0.45) return 0.45 - (t - 0.2) * 1.4; // the dip
    return 0.1 + (t - 0.45) * 1.5; // recovery above start
  });
  const xAt = (i: number) => left + ((right - left) * i) / (n - 1);
  const yAt = (i: number) => bottom - (bottom - top) * Math.max(0, Math.min(1, profile[i]));
  let path = `M${xAt(0)} ${yAt(0)}`;
  for (let i = 1; i < n; i++) path += ` L${xAt(i)} ${yAt(i)}`;
  let pts = "";
  nodes.forEach((label, i) => {
    const x = xAt(i),
      y = yAt(i);
    pts += `<circle cx="${x}" cy="${y}" r="4" fill="#002147"/>`;
    const lines = wrap(label, 12, 2);
    const ly = y < (top + bottom) / 2 ? y - 12 - (lines.length - 1) * 11 : y + 16;
    pts += lines
      .map((ln, k) => `<text x="${x}" y="${ly + k * 11}" text-anchor="middle" font-family="'EB Garamond',serif" font-size="10.5" fill="#6b6354">${esc(ln)}</text>`)
      .join("");
  });
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" xmlns="http://www.w3.org/2000/svg">
    <text x="20" y="${top}" font-family="'EB Garamond',serif" font-style="italic" font-size="12" fill="#6D1D2C" transform="rotate(-90 20 ${(top + bottom) / 2})">morale & performance →</text>
    <path d="${path}" fill="none" stroke="#6D1D2C" stroke-width="2"/>
    ${pts}
  </svg>`;
}

// ----------------------------------------------------------- flow of nodes
export function svgFlowNodes(nodes: string[]): string {
  const list = nodes && nodes.length ? nodes : ["Observe", "Name", "Choose"];
  const n = list.length;
  const W = 660,
    arrowW = 22,
    pad = 8;
  const boxW = Math.floor((W - (n - 1) * arrowW - pad * 2) / n);
  const boxH = 84,
    y = 24;
  const charsPerLine = Math.max(8, Math.floor(boxW / 7));
  let out = "";
  list.forEach((label, i) => {
    const x = pad + i * (boxW + arrowW);
    const fill = i % 2 === 0 ? "rgba(0,33,71,.05)" : "rgba(109,29,44,.05)";
    out += `<rect x="${x}" y="${y}" width="${boxW}" height="${boxH}" rx="9" fill="${fill}" stroke="#C7B79B"/>`;
    out += multilineText(wrap(label, charsPerLine, 3), x + boxW / 2, y + boxH / 2, "#2A2520", 13, "600");
    if (i < n - 1) {
      const ax = x + boxW + arrowW / 2;
      out += `<text x="${ax}" y="${y + boxH / 2 + 7}" text-anchor="middle" fill="#FF600F" font-size="20" font-weight="700">→</text>`;
    }
  });
  return `<svg viewBox="0 0 ${W} ${boxH + 48}" width="100%" xmlns="http://www.w3.org/2000/svg">${out}</svg>`;
}

// ----------------------------------------------------------- selectors
export function heroSvg(fw: Framework): string {
  switch (fw.hero) {
    case "clark":
      return svgStairs();
    case "quad":
      return fw.quad ? svgQuad(fw.quad) : svgFlowNodes(fw.nodes || []);
    case "equation":
      return svgChangeEquation();
    case "trustEq":
      return svgTrustEquation();
    case "layers":
      return svgLayers(fw.nodes || []);
    case "pyramid":
      return svgPyramid(fw.nodes || []);
    case "curve":
      return svgCurve(fw.nodes || []);
    default:
      return svgFlowNodes(fw.nodes || []);
  }
}

export function heroCaption(fw: Framework): string {
  switch (fw.hero) {
    case "clark":
      return "The four stages ascend — each must hold before the next.";
    case "equation":
      return "Beckhard & Harris — whether resistance can be overcome.";
    case "trustEq":
      return "Maister's trust equation — the denominator does the work.";
    case "quad":
      return "Two axes, four positions.";
    case "layers":
      return "Layered — the deepest level drives the rest.";
    case "pyramid":
      return "A pyramid — the base carries everything above it.";
    case "curve":
      return "The emotional transition — change includes loss.";
    default:
      return "The shape of the idea.";
  }
}

export function thumbSvg(fw: Framework): string {
  const c = "#7d93c0";
  switch (fw.hero) {
    case "clark":
      return `<svg width="64" height="48" viewBox="0 0 64 48" fill="none"><rect x="8" y="30" width="14" height="12" rx="2" fill="${c}"/><rect x="25" y="22" width="14" height="20" rx="2" fill="#5a73a8"/><rect x="42" y="12" width="14" height="30" rx="2" fill="#002147"/></svg>`;
    case "quad":
      return `<svg width="56" height="48" viewBox="0 0 56 48" fill="none"><rect x="6" y="6" width="20" height="16" fill="rgba(0,33,71,.12)" stroke="${c}"/><rect x="30" y="6" width="20" height="16" fill="rgba(109,29,44,.12)" stroke="${c}"/><rect x="6" y="26" width="20" height="16" fill="rgba(109,29,44,.1)" stroke="${c}"/><rect x="30" y="26" width="20" height="16" fill="rgba(0,33,71,.1)" stroke="${c}"/></svg>`;
    case "equation":
      return `<svg width="72" height="40" viewBox="0 0 72 40" fill="none"><text x="2" y="27" font-family="serif" font-size="18" font-weight="700" fill="#002147">D×V×N</text><text x="56" y="27" font-family="serif" font-size="18" font-weight="700" fill="#6D1D2C">&gt;R</text></svg>`;
    case "trustEq":
      return `<svg width="64" height="44" viewBox="0 0 64 44" fill="none"><text x="32" y="20" text-anchor="middle" font-family="serif" font-size="13" font-weight="700" fill="#002147">C+R+I</text><line x1="14" y1="26" x2="50" y2="26" stroke="#6D1D2C" stroke-width="1.4"/><text x="32" y="40" text-anchor="middle" font-family="serif" font-size="12" fill="#6D1D2C">SO</text></svg>`;
    case "layers":
      return `<svg width="60" height="48" viewBox="0 0 60 48" fill="none"><rect x="8" y="8" width="44" height="10" rx="2" fill="#cfd9ea"/><rect x="8" y="20" width="44" height="10" rx="2" fill="#7d93c0"/><rect x="8" y="32" width="44" height="10" rx="2" fill="#002147"/></svg>`;
    case "pyramid":
      return `<svg width="60" height="48" viewBox="0 0 60 48" fill="none"><path d="M22 8 L38 8 L42 20 L18 20 Z" fill="#002147"/><path d="M18 22 L42 22 L46 34 L14 34 Z" fill="#7d93c0"/><path d="M14 36 L46 36 L50 44 L10 44 Z" fill="#cfd9ea"/></svg>`;
    case "curve":
      return `<svg width="64" height="44" viewBox="0 0 64 44" fill="none"><path d="M6 14 C16 14 18 36 30 36 C44 36 46 8 58 8" stroke="#6D1D2C" stroke-width="2" fill="none"/></svg>`;
    default:
      return `<svg width="64" height="40" viewBox="0 0 64 40" fill="none"><rect x="4" y="14" width="14" height="12" rx="2" fill="${c}"/><rect x="25" y="14" width="14" height="12" rx="2" fill="#5a73a8"/><rect x="46" y="14" width="14" height="12" rx="2" fill="#002147"/><text x="20" y="24" fill="#FF600F" font-size="11" font-weight="700">→</text><text x="41" y="24" fill="#FF600F" font-size="11" font-weight="700">→</text></svg>`;
  }
}
